import React from 'react';
import { useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/Skeleton';
import { Folder, FolderKanban, Clock, Link as LinkIcon } from 'lucide-react';
import { formatDate } from '@/lib/utils';

const ProjectsTab = ({ projects = [], loading = false, onNavigate }) => {
  const navigate = useNavigate();

  const handleNavigate = (path) => {
    if (onNavigate) {
      onNavigate(path);
    } else {
      navigate(path);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {[1, 2, 3, 4].map((i) => (
          <Card key={i} className="overflow-hidden">
            <CardContent className="p-6">
              <Skeleton className="h-12 w-12 rounded-2xl mb-4" />
              <Skeleton className="h-6 w-3/4 mb-2" />
              <Skeleton className="h-5 w-20 mb-4" />
              <Skeleton className="h-4 w-full mb-2" />
              <Skeleton className="h-4 w-5/6 mb-6" />
              <div className="flex items-center justify-between pt-4 border-t border-white/10">
                <Skeleton className="h-4 w-24" />
                <Skeleton className="h-4 w-20" />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  // Empty state
  if (!projects || projects.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <div className="h-16 w-16 rounded-2xl bg-white/10 flex items-center justify-center mb-4">
          <FolderKanban className="h-8 w-8 text-white/60" />
        </div>
        <h3 className="text-xl font-semibold text-foreground/90 mb-2">
          No projects yet
        </h3>
        <p className="text-muted-foreground">
          Create a project to get started
        </p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {projects.map((project, index) => {
        // Determine color styling
        const colorStyle = {};
        let colorClass = '';

        if (project.color) {
          if (project.color.startsWith('#')) {
            colorStyle.backgroundColor = project.color;
          } else if (project.color.startsWith('bg-')) {
            colorClass = project.color;
          } else {
            // Fallback for other color formats
            colorStyle.backgroundColor = project.color;
          }
        }

        return (
          <Card
            key={project.id}
            className={cn(
              "hover:shadow-glass-lg transition-all duration-300 cursor-pointer hover:-translate-y-1 animate-fade-up overflow-hidden"
            )}
            style={{ animationDelay: `${index * 60}ms` }}
            onClick={() => handleNavigate(`/projects/${project.id}`)}
          >
            <CardContent className="p-6 flex flex-col h-full">
              {/* Color icon */}
              <div
                className={cn(
                  "h-12 w-12 rounded-2xl flex items-center justify-center text-white shadow-lg mb-4",
                  colorClass || (!project.color && "bg-blue-500")
                )}
                style={Object.keys(colorStyle).length > 0 ? colorStyle : undefined}
              >
                <Folder className="h-6 w-6" />
              </div>

              {/* Project name */}
              <h3 className="font-semibold text-lg mb-2 text-foreground/90">
                {project.name}
              </h3>

              {/* Status badge */}
              {project.status && (
                <Badge variant="glass" className="mb-3 w-fit">
                  {project.status}
                </Badge>
              )}

              {/* Description */}
              {project.description && (
                <p className="text-sm text-muted-foreground mb-6 flex-1 line-clamp-3 leading-relaxed">
                  {project.description}
                </p>
              )}

              {/* Footer */}
              <div className="flex items-center justify-between pt-4 border-t border-white/10 mt-auto">
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <Clock className="h-3.5 w-3.5" />
                  <span>{formatDate(project.created_at)}</span>
                </div>
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <LinkIcon className="h-3.5 w-3.5" />
                  <span>sources</span>
                </div>
              </div>
            </CardContent>
          </Card>
        );
      })}

      {/* View all projects card */}
      <Card
        className={cn(
          "border-dashed border-white/30 bg-white/20 hover:bg-white/30 transition-all duration-300 cursor-pointer hover:-translate-y-1 animate-fade-up overflow-hidden"
        )}
        style={{ animationDelay: `${projects.length * 60}ms` }}
        onClick={() => handleNavigate('/projects')}
      >
        <CardContent className="p-6 flex items-center justify-center h-full min-h-[200px]">
          <div className="text-center">
            <p className="text-lg font-medium text-foreground/70">
              View all projects →
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ProjectsTab;
